export default {
  id: "quarterly",
  title: "Quarterly Reports & Submission Tracker",
  section: "Billing & Reports",
  order: 54,
  keywords: "quarterly report AI summary support coordinator email draft final upload tracker PBA HHS ELS",
  body: `
## What it is

DSPD clients need a narrative progress report every quarter, sent to their
support coordinator. The Quarterly Report screen writes most of it for you:
it gathers the quarter's shift documentation and drafts the narrative with
AI for you to edit and finalize. The Submission Tracker is the checklist
showing which clients still need theirs.

## How it works

The generator reads every **approved shift** for the client in the range —
shift notes, clock-out question answers, goal answers, and who worked — and
produces:

- **"Goals, Progress, and Staff Support"** — narrative paragraph one.
- **"Barriers, Behaviors, and Supervision Needs"** — paragraph two.
- Optional **per-service-code summaries** when multiple codes are included.
- The goals text itself, auto-filled from the client's linked goals.

Everything is a **draft for you to edit** — character counts are shown, and
nothing leaves the building until you mark it final. Reports save per client
per period, with a history of previous reports.

## Step by step: producing a quarterly

1. Open **Reports → Quarterly Report**.
2. Pick the **client**.
3. Pick the quarter with the quick buttons (current and prior year Q1–Q4)
   or set a custom range.
4. Select the **service codes** — the list only offers codes that actually
   have approved shifts in the range. Heads-up: mixing **PBA, HHS, or ELS**
   with other codes triggers a warning, because those typically require
   their own separate report — generate them separately.
5. Click **Generate** and wait for the draft.
6. **Edit every narrative field.** The AI drafts from real shift data but
   you are the author — check names, events, and tone.
7. **Save Draft** while working. When it's ready, **Save as Final**.
   (Regenerate throws away edits and re-drafts from scratch.)
8. **Download PDF** to review the final layout.
9. Click **Email** — the support coordinator's name and address pre-fill,
   the PDF attaches, and the send is logged in the report's history. Only
   final reports can be emailed.

### If the report was written outside the app

Use **Upload PDF** instead: the uploaded file is stored as that quarter's
final report immediately (no editable sections) and can be downloaded or
emailed the same way.

## Step by step: working the quarter's checklist

1. In **Reports**, pick **"Quarterly Submission Tracker"** and select the
   quarter.
2. Every DSPD/Private-Pay client appears with a status: **Missing** (no
   report), **Draft**, **Final - Not Sent**, or **Sent**.
3. Filter by status to build the to-do list — e.g. Missing + Draft is
   "still to write," Final - Not Sent is "ready to email."
4. Filter by support coordinator to batch the sends, or by client to check
   one case.
`,
};
